/**
 * Audio engine — thin Web Audio wrapper.
 *
 * Responsibilities:
 *  - Lazy AudioContext creation (browsers gate audio on a user gesture)
 *  - Master gain with ramped volume (no zipper noise, no clicks)
 *  - Mute + volume persistence to localStorage
 *  - Graceful no-op on environments without Web Audio
 *
 * Per AESTHETIC.md §7: warm, never sharp; never a "game over" sting.
 * LUFS target ~-24 — we keep the master gain modest by default (0.7).
 *
 * Notes:
 *  - `cueBus` is what cue oscillators connect to. It sits under `master` so
 *    the ambient bed can route through a separate `ambientBus` and we can
 *    rebalance the two without rewriting cues.
 */

const STORAGE_KEY = 'fernhollow-audio';
const DEFAULT_VOLUME = 0.7;
const RAMP_SECONDS = 0.05; // 50ms — fast enough to feel immediate, slow enough to avoid clicks

interface PersistedAudioState {
  muted: boolean;
  volume: number;
}

export interface AudioEngine {
  unlock(): Promise<void>;
  isUnlocked(): boolean;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  setVolume(v: number): void;
  getVolume(): number;
  /** Internal — used by synth + ambient modules. Returns null until unlocked. */
  context(): AudioContext | null;
  /** Bus for one-shot cues. Null until unlocked. */
  cueBus(): GainNode | null;
  /** Bus for the ambient bed (separately gain-staged). Null until unlocked. */
  ambientBus(): GainNode | null;
}

interface AudioInternals {
  ctx: AudioContext;
  master: GainNode;
  cues: GainNode;
  ambient: GainNode;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function loadPersistedState(): PersistedAudioState {
  if (typeof localStorage === 'undefined') {
    return { muted: false, volume: DEFAULT_VOLUME };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { muted: false, volume: DEFAULT_VOLUME };
    const parsed = JSON.parse(raw) as Partial<PersistedAudioState>;
    const muted = typeof parsed.muted === 'boolean' ? parsed.muted : false;
    const rawVol = typeof parsed.volume === 'number' ? parsed.volume : DEFAULT_VOLUME;
    const volume = Math.min(1, Math.max(0, rawVol));
    return { muted, volume };
  } catch {
    return { muted: false, volume: DEFAULT_VOLUME };
  }
}

function persistState(state: PersistedAudioState): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be full or disabled — fail silently; audio still works in-session.
  }
}

class FernhollowAudioEngine implements AudioEngine {
  private state: PersistedAudioState;
  private internals: AudioInternals | null = null;
  private unlockPromise: Promise<void> | null = null;
  private supported: boolean;

  constructor() {
    this.state = loadPersistedState();
    this.supported = getAudioContextCtor() !== null;
  }

  isUnlocked(): boolean {
    return this.internals !== null;
  }

  context(): AudioContext | null {
    return this.internals?.ctx ?? null;
  }

  cueBus(): GainNode | null {
    return this.internals?.cues ?? null;
  }

  ambientBus(): GainNode | null {
    return this.internals?.ambient ?? null;
  }

  unlock(): Promise<void> {
    if (!this.supported) return Promise.resolve();
    if (this.internals) return Promise.resolve();
    if (this.unlockPromise) return this.unlockPromise;

    this.unlockPromise = this.createContext();
    return this.unlockPromise;
  }

  setMuted(muted: boolean): void {
    this.state = { ...this.state, muted };
    persistState(this.state);
    this.applyMasterGain();
  }

  isMuted(): boolean {
    return this.state.muted;
  }

  setVolume(v: number): void {
    const clamped = Math.min(1, Math.max(0, v));
    this.state = { ...this.state, volume: clamped };
    persistState(this.state);
    this.applyMasterGain();
  }

  getVolume(): number {
    return this.state.volume;
  }

  private async createContext(): Promise<void> {
    const Ctor = getAudioContextCtor();
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      // On Safari/iOS the context starts in 'suspended' state even after a user
      // gesture; resume() is the canonical handshake.
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {
          // If resume fails we still keep the context — cues will be silent
          // but no exceptions surface to callers.
        }
      }

      const master = ctx.createGain();
      master.connect(ctx.destination);

      const cues = ctx.createGain();
      cues.gain.value = 1.0;
      cues.connect(master);

      const ambient = ctx.createGain();
      // Ambient sits at ~0.15 of the cue bus — texture, never foregrounded.
      ambient.gain.value = 0.15;
      ambient.connect(master);

      this.internals = { ctx, master, cues, ambient };
      this.applyMasterGain();
    } catch {
      // Some embedded browsers throw on construction; degrade to silent.
      this.supported = false;
      this.internals = null;
    }
  }

  private applyMasterGain(): void {
    if (!this.internals) return;
    const { ctx, master } = this.internals;
    const target = this.state.muted ? 0 : this.state.volume;
    const now = ctx.currentTime;
    // setTargetAtTime gives an exponential approach with no audible click.
    try {
      master.gain.cancelScheduledValues(now);
      master.gain.setTargetAtTime(target, now, RAMP_SECONDS);
    } catch {
      // Some implementations balk at cancelScheduledValues mid-ramp; fall back.
      master.gain.value = target;
    }
  }
}

export const engine: AudioEngine = new FernhollowAudioEngine();
